// src/ui/theme/styleTemplateIO.js
// Conversion entre el mapa de tokens (runtime) y el JSON de la propuesta
// (entregable para el dev). Aislado aqui para poder TESTEARLO: el JSON es lo que
// materializa el dev, asi que su round-trip debe ser sin perdidas.
//
// Formato JSON:
//   { name?, base: {colors,fonts,radius,images,sidebar,texts},
//     layers: { teacher: { pages: { "<scope>": {colors,...} } } } }
//
// Reglas: cada token "<prefijo>.<resto>" cae en su seccion por prefijo. El
// "resto" puede contener puntos (p.ej. text."salas.guide.step1.title"): se
// preserva tal cual al cortar solo el prefijo.

export function tokenMapToData(map) {
  const colors = {};
  const fonts = {};
  const radius = {};
  const images = {};
  const sidebar = {};
  const texts = {};
  Object.entries(map || {}).forEach(([token, value]) => {
    if (token.startsWith("color.")) colors[token.slice(6)] = value;
    else if (token.startsWith("font.")) fonts[token.slice(5)] = value;
    else if (token.startsWith("radius.")) radius[token.slice(7)] = value;
    else if (token.startsWith("image.")) images[token.slice(6)] = value;
    else if (token.startsWith("sidebar.")) sidebar[token.slice(8)] = value;
    else if (token.startsWith("text.")) texts[token.slice(5)] = value;
  });
  return { colors, fonts, radius, images, sidebar, texts };
}

export function dataToTokenMap(section) {
  const out = {};
  if (!section) return out;
  Object.entries(section.colors || {}).forEach(([k, v]) => {
    out[`color.${k}`] = v;
  });
  Object.entries(section.fonts || {}).forEach(([k, v]) => {
    out[`font.${k}`] = v;
  });
  Object.entries(section.radius || {}).forEach(([k, v]) => {
    out[`radius.${k}`] = v;
  });
  Object.entries(section.images || {}).forEach(([k, v]) => {
    out[`image.${k}`] = v;
  });
  Object.entries(section.sidebar || {}).forEach(([k, v]) => {
    out[`sidebar.${k}`] = v;
  });
  Object.entries(section.texts || {}).forEach(([k, v]) => {
    out[`text.${k}`] = v;
  });
  return out;
}

export function buildTemplateData(base, pageOverrides) {
  const pages = {};
  Object.entries(pageOverrides || {}).forEach(([pageKey, map]) => {
    if (map && Object.keys(map).length) pages[pageKey] = tokenMapToData(map);
  });
  return {
    base: tokenMapToData(base),
    layers: { teacher: { pages } },
  };
}

export function dataToOverrides(data) {
  const base = dataToTokenMap(data?.base);
  const rawPages = data?.layers?.teacher?.pages || {};
  const pages = {};
  Object.entries(rawPages).forEach(([k, v]) => {
    pages[k] = dataToTokenMap(v);
  });
  return { base, pages };
}

// Prefijos de token que el JSON sabe serializar.
export const KNOWN_TOKEN_PREFIXES = [
  "color.",
  "font.",
  "radius.",
  "image.",
  "sidebar.",
  "text.",
];

// Tokens que ESCRIBE un control (los que acaban en el JSON). Util para el editor
// y para auditar cobertura. Devuelve siempre tokens completos (con prefijo).
export function controlTokens(ctrl) {
  if (!ctrl) return [];
  switch (ctrl.kind) {
    case "token":
    case "choice":
    case "number":
      return ctrl.token ? [ctrl.token] : [];
    case "image":
      return ctrl.slot ? [`image.${ctrl.slot}`] : [];
    case "text":
      return ctrl.slot ? [`text.${ctrl.slot}`] : [];
    case "typography": {
      if (ctrl.tokens) return Object.values(ctrl.tokens);
      const r = ctrl.role;
      return [
        `font.${r}`,
        `font.${r}Weight`,
        `font.${r}Style`,
        `font.${r}Scale`,
      ];
    }
    default:
      return [];
  }
}

const styleTemplateIO = {
  tokenMapToData,
  dataToTokenMap,
  buildTemplateData,
  dataToOverrides,
  controlTokens,
  KNOWN_TOKEN_PREFIXES,
};

export default styleTemplateIO;
